/*
 * Shared client telemetry, harvested from the retired /telemetry page and
 * redistributed as ambient live detail across the site.
 *
 * One requestAnimationFrame loop counts frames; once a second it computes the
 * frame rate and refreshes the cheap readings (heap, network, viewport,
 * uptime). Every value is measured in this browser and rendered here; nothing
 * is sent, stored or logged.
 *
 * Consumers call acquireTelemetry() in mounted() and releaseTelemetry() in
 * beforeUnmount(). The loop runs only while at least one consumer is live,
 * so an idle tree costs nothing.
 */
import { reactive } from 'vue';

export interface TelemetryState {
  /** Measured frames per second, sampled at 1 Hz. */
  fps: number;
  /** Rolling 1 Hz fps samples, newest last. Capped at 90. */
  fpsHistory: number[];
  /** Used JS heap in MB. Chromium only; null elsewhere. */
  heapUsedMb: number | null;
  /** Network Information API. Null where the browser does not expose it. */
  netType: string | null;
  downlink: number | null;
  rtt: number | null;
  online: boolean;
  /** Battery Status API. Null where unavailable. */
  batteryLevel: number | null;
  batteryCharging: boolean;
  /** Static device facts, read once. */
  gpu: string | null;
  platform: string | null;
  cores: number | null;
  memoryGb: number | null;
  engine: string | null;
  timezone: string | null;
  /** Window and screen. */
  viewportW: number;
  viewportH: number;
  dpr: number;
  /** Navigation timing for this page load. */
  loadMs: number | null;
  ttfbMs: number | null;
  /** Time since the app booted, as hh:mm:ss. */
  uptime: string;
  uptimeSec: number;
}

const state = reactive<TelemetryState>({
  fps: 0,
  fpsHistory: [],
  heapUsedMb: null,
  netType: null,
  downlink: null,
  rtt: null,
  online: true,
  batteryLevel: null,
  batteryCharging: false,
  gpu: null,
  platform: null,
  cores: null,
  memoryGb: null,
  engine: null,
  timezone: null,
  viewportW: 0,
  viewportH: 0,
  dpr: 1,
  loadMs: null,
  ttfbMs: null,
  uptime: '00:00:00',
  uptimeSec: 0,
});

const sessionStart = performance.now();

let consumers = 0;
let rafId = 0;
let frames = 0;
let lastSample = 0;
let staticsRead = false;
let batteryHooked = false;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function readGpu(): string | null {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return null;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) return null;
    const raw = gl.getParameter((ext as any).UNMASKED_RENDERER_WEBGL);
    return raw ? String(raw) : null;
  } catch {
    return null;
  }
}

function readEngine(): string | null {
  const ua = navigator.userAgent;
  const match =
    /(Firefox)\/([\d.]+)/.exec(ua) ||
    /(Edg)\/([\d.]+)/.exec(ua) ||
    /(Chrome)\/([\d.]+)/.exec(ua) ||
    /Version\/([\d.]+).*(Safari)/.exec(ua);
  if (!match) return null;
  return match[1] === 'Edg' ? `Edge ${match[2]}` : `${match[1]} ${match[2]}`;
}

function readStatics() {
  if (staticsRead) return;
  staticsRead = true;
  const nav = navigator as any;
  state.gpu = readGpu();
  state.platform = nav.userAgentData?.platform || navigator.platform || null;
  state.cores = navigator.hardwareConcurrency || null;
  state.memoryGb = typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null;
  state.engine = readEngine();
  try {
    state.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    state.timezone = null;
  }
}

function readTiming() {
  const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  if (!entry) return;
  state.ttfbMs = Math.max(0, Math.round(entry.responseStart - entry.requestStart));
  if (entry.loadEventEnd > 0) {
    state.loadMs = Math.max(0, Math.round(entry.loadEventEnd - entry.startTime));
  }
}

function hookBattery() {
  if (batteryHooked) return;
  batteryHooked = true;
  const nav = navigator as any;
  if (!nav.getBattery) return;
  nav
    .getBattery()
    .then((b: any) => {
      const apply = () => {
        state.batteryLevel = Math.round(b.level * 100);
        state.batteryCharging = Boolean(b.charging);
      };
      apply();
      // The battery manager is a browser-global singleton; these listeners
      // are attached once for the life of the page and update the store only.
      b.addEventListener('levelchange', apply);
      b.addEventListener('chargingchange', apply);
    })
    .catch(() => {
      /* battery status unavailable */
    });
}

function onResize() {
  state.viewportW = window.innerWidth;
  state.viewportH = window.innerHeight;
  state.dpr = Math.round(window.devicePixelRatio * 100) / 100;
}

function sample() {
  const nav = navigator as any;
  const perf = performance as any;

  if (perf.memory) {
    state.heapUsedMb = perf.memory.usedJSHeapSize / 1048576;
  }

  const c = nav.connection || nav.mozConnection || nav.webkitConnection;
  state.netType = c?.effectiveType || null;
  state.downlink = typeof c?.downlink === 'number' ? c.downlink : null;
  state.rtt = typeof c?.rtt === 'number' ? c.rtt : null;
  state.online = navigator.onLine;

  const sec = Math.floor((performance.now() - sessionStart) / 1000);
  state.uptimeSec = sec;
  state.uptime = `${pad(Math.floor(sec / 3600))}:${pad(Math.floor((sec % 3600) / 60))}:${pad(sec % 60)}`;

  if (state.loadMs === null) readTiming();
}

function loop(now: number) {
  frames += 1;
  const delta = now - lastSample;
  if (delta >= 1000) {
    // A long gap means the tab was hidden and rAF was throttled; the frame
    // count over that window is not a frame rate, so skip the sample.
    if (delta < 2000) {
      state.fps = Math.round((frames * 1000) / delta);
      state.fpsHistory.push(state.fps);
      if (state.fpsHistory.length > 90) state.fpsHistory.shift();
    }
    frames = 0;
    lastSample = now;
    sample();
  }
  rafId = requestAnimationFrame(loop);
}

export function acquireTelemetry(): TelemetryState {
  consumers += 1;
  if (consumers === 1) {
    readStatics();
    hookBattery();
    onResize();
    sample();
    window.addEventListener('resize', onResize);
    frames = 0;
    lastSample = performance.now();
    rafId = requestAnimationFrame(loop);
  }
  return state;
}

export function releaseTelemetry() {
  consumers = Math.max(0, consumers - 1);
  if (consumers === 0) {
    cancelAnimationFrame(rafId);
    window.removeEventListener('resize', onResize);
  }
}

export const telemetry = state;
